import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import Badge from "components/Badge";
import { IconExternalLink } from "@tabler/icons-react";
import { getCurrentReferenceUnitValue } from "../../MappingEditor/utils";

export interface EPDGroupColumns {
  factor?: number | null;
  epd_name: string;
  database?: string;
  dataset_source?: string;
  dataset_url?: string;
  recyclability_score?: string | number;
  recyclability_qualification?: string;
  reference_unit?: string;
  reference_size?: string | number;
  material_name?: string;
  conformity?: string;
  mass_value_m?: string | number;
  mass_value_m2?: string | number;
  mass_value_m3?: string | number;
}

const DEFAULT_COLUMNS = [
  "epd_name",
  "factor",
  "database",
  "material_name",
  "recyclability_score",
  "recyclability_qualification",
  "reference_unit",
  "reference_size",
  "conformity",
  "mass_value_m",
  "mass_value_m2",
  "mass_value_m3",
];

const useDatasetViewTableColumns = (referenceUnitID?: string) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const databaseName = (database?: string, source_name?: string) => {
    if (database === "-" && source_name === "CUSTOM")
      return t("EPDEditor.EPDFile");
    if (database === "-") return t("productEditor.unknown");
    if (database)
      return `${database.charAt(0).toUpperCase()}${database
        .slice(1)
        .toLowerCase()}`;
  };

  function cell(item: EPDGroupColumns, key: string) {
    if (key === "database") {
      if (
        item.dataset_source?.toLowerCase().includes("custom") &&
        item.dataset_url
      ) {
        return databaseChip(item);
      }

      if (item.database === "-") {
        return "-";
      }

      return databaseChip(item);
    }
    return item[key as keyof EPDGroupColumns];
  }

  function databaseChip(item: EPDGroupColumns) {
    return (
      <Badge type="auto" className="!h-5" size="small">
        {databaseName(item.database, item.dataset_source)}
        {item.dataset_url && (
          <a
            href={item.dataset_url}
            target="_blank"
            rel={"noreferrer"}
            className="md:flex items-center cursor-pointer"
          >
            <IconExternalLink width={18} className="ml-1" stroke={3} />
          </a>
        )}
      </Badge>
    );
  }

  function getReferenceUnit(reference_unit: string | undefined) {
    const currentValue = getCurrentReferenceUnitValue(reference_unit);
    return isEng ? currentValue?.name : currentValue?.name_de;
  }

  function columnCell(row: EPDGroupColumns, key: string) {
    if (key === "reference_unit") {
      return <div>{getReferenceUnit(row.reference_unit)}</div>;
    } else if (key === "factor") {
      return `${cell(row, key)} ${
        getReferenceUnit(row.reference_unit) ?? "-"
      }/${getReferenceUnit(referenceUnitID) ?? "-"}`;
    } else {
      return cell(row, key);
    }
  }

  function column(key: string, index: number) {
    return {
      accessorFn: (row: EPDGroupColumns) => row[key as keyof EPDGroupColumns],
      id: key,
      cell: (info: { row: { original: EPDGroupColumns } }) =>
        columnCell(info.row.original, key),
      header: () => <div className="truncate">{t(`mapping.${key}`)}</div>,
      enableColumnFilter: false,
      enableSorting: false,
      isSticky: index === 0,
      stickyClass: "bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.25)] z-2",
      size: index === 0 || index == 2 ? 150 : 0,
      ...(key === "database" && { maxWidth: "auto" }),
    };
  }

  return useMemo<ColumnDef<EPDGroupColumns>[]>(
    () => [...DEFAULT_COLUMNS.map((key, index) => column(key, index))],
    []
  );
};

export default useDatasetViewTableColumns;
